/**
 * @component BugReportModal
 * @description 4-Schritt-Funnel zur Bug-Erfassung mit Auto-Kontext (#943).
 *
 * Schritte:
 *   1. Trigger (Hinweis-Screen "Was ist passiert?")
 *   2. Textareas (was_passiert + was_sollte)
 *   3. Kontext + Schweregrad-Pflichtkarten (3 Cards: Kritisch/Wichtig/Kosmetisch)
 *   4. Success (BR-Nr + Notion-Link)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from './ui/Modal';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Bug, ChevronRight, ChevronLeft, CheckCircle2, ExternalLink, AlertOctagon, AlertTriangle, Sparkles, Upload, X, FileVideo, FileText, Image as ImageIcon } from 'lucide-react';

const MAX_FILES = 5;
const MAX_FILE_BYTES = 1024 * 1024 * 1024; // 1 GB
const ALLOWED_MIME = new Set([
  'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp',
  'video/mp4', 'video/quicktime', 'video/webm',
  'application/pdf',
]);

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

const API = import.meta.env.VITE_API_URL || '';

function getConsoleErrors() {
  if (typeof window !== 'undefined' && window.__weshipConsoleErrors) {
    return window.__weshipConsoleErrors.slice(-50).join('\n');
  }
  return '';
}

function getBrowserString() {
  const ua = navigator.userAgent || '';
  let name = 'Unknown';
  let version = '';
  if (/Edg\//.test(ua)) { name = 'Edge'; version = ua.match(/Edg\/([\d.]+)/)?.[1] || ''; }
  else if (/Chrome\//.test(ua)) { name = 'Chrome'; version = ua.match(/Chrome\/([\d.]+)/)?.[1] || ''; }
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) { name = 'Safari'; version = ua.match(/Version\/([\d.]+)/)?.[1] || ''; }
  else if (/Firefox\//.test(ua)) { name = 'Firefox'; version = ua.match(/Firefox\/([\d.]+)/)?.[1] || ''; }
  return `${name} ${version}`.trim();
}

const STEPS = ['trigger', 'describe', 'context', 'success'];

const SEVERITY_CARDS = [
  {
    key: 'kritisch',
    Icon: AlertOctagon,
    accent: 'rose',
    titleKey: 'bugReport.kritisch_title',
    descKey: 'bugReport.kritisch_desc',
  },
  {
    key: 'wichtig',
    Icon: AlertTriangle,
    accent: 'amber',
    titleKey: 'bugReport.wichtig_title',
    descKey: 'bugReport.wichtig_desc',
  },
  {
    key: 'kosmetisch',
    Icon: Sparkles,
    accent: 'emerald',
    titleKey: 'bugReport.kosmetisch_title',
    descKey: 'bugReport.kosmetisch_desc',
  },
];

const ACCENT_RING = {
  rose: 'border-rose-300 ring-2 ring-rose-200 bg-rose-50',
  amber: 'border-amber-300 ring-2 ring-amber-200 bg-amber-50',
  emerald: 'border-emerald-300 ring-2 ring-emerald-200 bg-emerald-50',
};
const ACCENT_ICON = {
  rose: 'text-rose-500',
  amber: 'text-amber-500',
  emerald: 'text-emerald-500',
};
const ACCENT_BORDER = {
  rose: 'border-rose-200 hover:border-rose-300',
  amber: 'border-amber-200 hover:border-amber-300',
  emerald: 'border-emerald-200 hover:border-emerald-300',
};

export default function BugReportModal({ open, onClose }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [titel, setTitel] = useState('');
  const [wasPassiert, setWasPassiert] = useState('');
  const [wasSollte, setWasSollte] = useState('');
  const [schweregrad, setSchweregrad] = useState(null); // PFLICHT, kein Default
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0); // 0..100, nur waehrend Upload
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]); // [{file, preview, error}]
  const [dragOver, setDragOver] = useState(false);
  const titelRef = useRef(null);
  const fileInputRef = useRef(null);

  const ctx = useMemo(() => ({
    page_url: typeof window !== 'undefined' ? window.location.href : '',
    browser: getBrowserString(),
    viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
    console_errors: getConsoleErrors(),
  }), [open]);

  useEffect(() => {
    if (!open) {
      setStep(0);
      setTitel('');
      setWasPassiert('');
      setWasSollte('');
      setSchweregrad(null);
      setResult(null);
      setError(null);
      setSubmitting(false);
      setProgress(0);
      setFiles((prev) => {
        prev.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
        return [];
      });
      return;
    }
    if (step === 1 && titelRef.current) setTimeout(() => titelRef.current?.focus(), 80);
  }, [open, step]);

  function addFiles(fileList) {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;
    setError(null);
    setFiles((prev) => {
      const next = [...prev];
      for (const f of incoming) {
        if (next.length >= MAX_FILES) {
          setError(t('bugReport.upload_max_files').replace('{n}', MAX_FILES));
          break;
        }
        if (!ALLOWED_MIME.has(f.type)) {
          setError(t('bugReport.upload_mime_invalid').replace('{name}', f.name));
          continue;
        }
        if (f.size > MAX_FILE_BYTES) {
          setError(t('bugReport.upload_too_large').replace('{name}', f.name));
          continue;
        }
        const preview = f.type.startsWith('image/') ? URL.createObjectURL(f) : null;
        next.push({ file: f, preview });
      }
      return next;
    });
  }

  function removeFile(idx) {
    setFiles((prev) => {
      const next = [...prev];
      const removed = next.splice(idx, 1)[0];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return next;
    });
  }

  function handlePaste(e) {
    const items = e.clipboardData?.items;
    if (!items || items.length === 0) return;
    const pasted = [];
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (!file) continue;
        const ext = (item.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
        const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        pasted.push(new File([file], `screenshot-${ts}.${ext}`, { type: item.type }));
      }
    }
    if (pasted.length > 0) {
      e.preventDefault();
      addFiles(pasted);
    }
  }

  async function submit() {
    if (!titel.trim()) { setError(t('bugReport.titel_required')); return; }
    if (!schweregrad) { setError(t('bugReport.schweregrad_required')); return; }
    setSubmitting(true);
    setError(null);
    setProgress(0);

    const hasFiles = files.length > 0;

    try {
      let json;
      if (hasFiles) {
        // multipart upload mit Progress via XHR
        const fd = new FormData();
        fd.append('bug_titel', titel.trim());
        fd.append('schweregrad', schweregrad);
        if (wasPassiert.trim()) fd.append('was_passiert', wasPassiert.trim());
        if (wasSollte.trim()) fd.append('was_sollte', wasSollte.trim());
        if (ctx.page_url) fd.append('page_url', ctx.page_url);
        if (ctx.browser) fd.append('browser', ctx.browser);
        if (ctx.viewport) fd.append('viewport', ctx.viewport);
        if (ctx.console_errors) fd.append('console_errors', ctx.console_errors);
        for (const { file } of files) fd.append('files', file, file.name);

        json = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${API}/api/bug-reports`);
          xhr.withCredentials = true;
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
          };
          xhr.onload = () => {
            try {
              const parsed = JSON.parse(xhr.responseText);
              if (xhr.status >= 200 && xhr.status < 300) resolve(parsed);
              else reject(new Error(parsed.error || `HTTP ${xhr.status}`));
            } catch { reject(new Error(`HTTP ${xhr.status}`)); }
          };
          xhr.onerror = () => reject(new Error('network_error'));
          xhr.send(fd);
        });
      } else {
        const res = await fetch(`${API}/api/bug-reports`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bug_titel: titel.trim(),
            schweregrad,
            was_passiert: wasPassiert.trim() || null,
            was_sollte: wasSollte.trim() || null,
            page_url: ctx.page_url,
            browser: ctx.browser,
            viewport: ctx.viewport,
            console_errors: ctx.console_errors || null,
          }),
        });
        json = await res.json();
        if (!res.ok || !json.ok) throw new Error(json.error || `HTTP ${res.status}`);
      }

      setResult(json);
      setStep(3);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
      setProgress(0);
    }
  }

  const stepName = STEPS[step];
  const canNextFromTrigger = true;
  const canNextFromDescribe = titel.trim().length > 0;
  const canSubmit = !!schweregrad && !submitting;

  function ProgressBar() {
    return (
      <div className="flex items-center gap-1.5 mb-4">
        {STEPS.slice(0, 3).map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? 'bg-[#338edc]' : 'bg-[var(--bg-inset)] border border-line'}`}
          />
        ))}
      </div>
    );
  }

  function Footer() {
    if (stepName === 'success') {
      return (
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:bg-[#2a7bc4] transition-colors"
        >
          {t('common.close')}
        </button>
      );
    }
    return (
      <>
        <button
          onClick={step === 0 ? onClose : () => setStep((s) => s - 1)}
          disabled={submitting}
          className="px-4 py-2 rounded-lg border border-line text-sm font-medium text-content-secondary hover:bg-surface-hover transition-colors disabled:opacity-50 inline-flex items-center gap-1"
        >
          {step === 0 ? t('common.cancel') : (<><ChevronLeft size={14} />{t('common.back')}</>)}
        </button>
        {stepName === 'context' ? (
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:bg-[#2a7bc4] transition-colors disabled:opacity-50 inline-flex items-center gap-1"
          >
            {submitting ? t('common.sending') : t('bugReport.submit')}
          </button>
        ) : (
          <button
            onClick={() => setStep((s) => s + 1)}
            disabled={(stepName === 'trigger' && !canNextFromTrigger) || (stepName === 'describe' && !canNextFromDescribe)}
            className="px-4 py-2 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:bg-[#2a7bc4] transition-colors disabled:opacity-50 inline-flex items-center gap-1"
          >
            {t('common.continue')}<ChevronRight size={14} />
          </button>
        )}
      </>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={stepName === 'success' ? t('bugReport.success_title') : t('bugReport.title')}
      size="lg"
      footer={<Footer />}
    >
      <div onPaste={handlePaste}>
      {stepName !== 'success' && <ProgressBar />}

      {stepName === 'trigger' && (
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#338edc]/10 flex items-center justify-center flex-shrink-0">
              <Bug size={22} className="text-[#338edc]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-base font-semibold text-content">{t('bugReport.trigger_title')}</div>
              <div className="text-sm text-content-secondary mt-1">{t('bugReport.trigger_subtitle')}</div>
            </div>
          </div>
          <div className="rounded-lg border border-line bg-[var(--bg-inset)] p-4 text-xs text-content-secondary space-y-1">
            <div>{t('bugReport.trigger_hint_1')}</div>
            <div>{t('bugReport.trigger_hint_2')}</div>
            <div>{t('bugReport.trigger_hint_3')}</div>
          </div>
        </div>
      )}

      {stepName === 'describe' && (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-content-secondary mb-1">
              {t('bugReport.titel_label')}
            </label>
            <input
              ref={titelRef}
              type="text"
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              maxLength={120}
              placeholder={t('bugReport.titel_placeholder')}
              className="w-full px-3 py-2 rounded-lg border border-line bg-surface-input text-sm text-content focus:outline-none focus:ring-2 focus:ring-[#338edc]/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-content-secondary mb-1">
              {t('bugReport.was_passiert_label')}
            </label>
            <textarea
              value={wasPassiert}
              onChange={(e) => setWasPassiert(e.target.value)}
              rows={5}
              placeholder={t('bugReport.was_passiert_placeholder')}
              style={{ minHeight: 120 }}
              className="w-full px-3 py-2 rounded-lg border border-line bg-surface-input text-sm text-content focus:outline-none focus:ring-2 focus:ring-[#338edc]/30 resize-y"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-content-secondary mb-1">
              {t('bugReport.was_sollte_label')}
            </label>
            <textarea
              value={wasSollte}
              onChange={(e) => setWasSollte(e.target.value)}
              rows={5}
              placeholder={t('bugReport.was_sollte_placeholder')}
              style={{ minHeight: 120 }}
              className="w-full px-3 py-2 rounded-lg border border-line bg-surface-input text-sm text-content focus:outline-none focus:ring-2 focus:ring-[#338edc]/30 resize-y"
            />
          </div>
        </div>
      )}

      {stepName === 'context' && (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-content-secondary mb-2">
              {t('bugReport.schweregrad_label')} <span className="text-rose-500">*</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {SEVERITY_CARDS.map((s) => {
                const selected = schweregrad === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setSchweregrad(s.key)}
                    className={`relative p-3 rounded-lg border text-left transition-all ${
                      selected ? ACCENT_RING[s.accent] : `bg-surface-card ${ACCENT_BORDER[s.accent]}`
                    }`}
                  >
                    <s.Icon size={18} className={`${ACCENT_ICON[s.accent]} mb-2`} />
                    <div className="text-sm font-semibold text-content">{t(s.titleKey)}</div>
                    <div className="text-[11px] text-content-secondary mt-0.5">{t(s.descKey)}</div>
                    {selected && (
                      <CheckCircle2 size={14} className={`${ACCENT_ICON[s.accent]} absolute top-2 right-2`} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-content-secondary mb-2">
              {t('bugReport.upload_label')}
              <span className="ml-2 text-[var(--text-tertiary)] font-normal">{t('bugReport.upload_hint')}</span>
            </label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                addFiles(e.dataTransfer.files);
              }}
              onClick={() => fileInputRef.current?.click()}
              className={`rounded-lg border border-dashed p-4 text-center cursor-pointer transition-colors ${
                dragOver ? 'border-[#338edc] bg-[#338edc]/5' : 'border-line bg-[var(--bg-inset)] hover:border-[#338edc]/40'
              }`}
            >
              <Upload size={20} className="mx-auto text-[var(--text-tertiary)]" />
              <div className="text-xs text-content-secondary mt-2">
                {t('bugReport.upload_dropzone')}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/png,image/jpeg,image/gif,image/webp,video/mp4,video/quicktime,video/webm,application/pdf"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                className="hidden"
              />
            </div>
            {files.length > 0 && (
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {files.map((f, i) => (
                  <div key={i} className="relative rounded-lg border border-line bg-surface-card p-2 flex items-center gap-2 min-w-0">
                    {f.preview ? (
                      <img src={f.preview} alt="" className="w-10 h-10 rounded-md object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-md bg-[var(--bg-inset)] flex items-center justify-center flex-shrink-0">
                        {f.file.type.startsWith('video/')
                          ? <FileVideo size={16} className="text-[var(--text-tertiary)]" />
                          : f.file.type.startsWith('image/')
                            ? <ImageIcon size={16} className="text-[var(--text-tertiary)]" />
                            : <FileText size={16} className="text-[var(--text-tertiary)]" />}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-content truncate">{f.file.name}</div>
                      <div className="text-[10px] text-[var(--text-tertiary)]">{formatBytes(f.file.size)}</div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                      className="flex-shrink-0 p-1 rounded-md hover:bg-rose-50 text-[var(--text-tertiary)] hover:text-rose-500 transition-colors"
                      aria-label={t('common.delete')}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {submitting && progress > 0 && (
              <div className="mt-2">
                <div className="h-1.5 rounded-full bg-[var(--bg-inset)] overflow-hidden">
                  <div className="h-full bg-[#338edc] transition-all" style={{ width: `${progress}%` }} />
                </div>
                <div className="text-[10px] text-content-secondary mt-1">{progress}%</div>
              </div>
            )}
          </div>

          <div>
            <div className="text-xs font-medium text-content-secondary mb-2">{t('bugReport.auto_kontext_label')}</div>
            <div className="rounded-lg border border-line bg-[var(--bg-inset)] p-3 space-y-1 font-mono text-[11px] text-[var(--text-tertiary)]">
              <div><span className="text-[var(--text-secondary)]">URL:</span> {ctx.page_url}</div>
              <div><span className="text-[var(--text-secondary)]">Browser:</span> {ctx.browser}</div>
              <div><span className="text-[var(--text-secondary)]">Viewport:</span> {ctx.viewport}</div>
              <div><span className="text-[var(--text-secondary)]">Reporter:</span> {user?.vorname} {user?.nachname} ({user?.email})</div>
              {ctx.console_errors && (
                <div className="pt-1 border-t border-line">
                  <span className="text-[var(--text-secondary)]">Console-Errors:</span>
                  <pre className="mt-1 whitespace-pre-wrap break-all max-h-24 overflow-y-auto">{ctx.console_errors}</pre>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</div>
          )}
        </div>
      )}

      {stepName === 'success' && result && (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 size={20} className="text-emerald-500" />
            </div>
            <div>
              <div className="text-base font-semibold text-content">{t('bugReport.success_msg')}</div>
              {result.br_nr && (
                <div className="text-sm text-content-secondary mt-1">
                  {t('bugReport.br_nr')}: <span className="font-mono font-semibold text-content">{result.br_nr}</span>
                </div>
              )}
            </div>
          </div>
          {result.notion_url && (
            <a
              href={result.notion_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-[#338edc] hover:underline"
            >
              {t('bugReport.open_notion')}<ExternalLink size={12} />
            </a>
          )}
          <div className="text-xs text-content-secondary">{t('bugReport.success_followup')}</div>
        </div>
      )}
      </div>
    </Modal>
  );
}
